// 월 달력 공용 내비게이션 — 회원 레슨/연습실/내 일정 달력의 헤더(◀ 제목 ▶)와 이동을 담당한다.
// 제목을 누르면 같은 자리에서 일 → 월(12칸) → 연(12칸) 보기로 전환되어 원하는 달로 바로 점프한다.
// ◀▶/스와이프는 현재 보기 단위로 이동한다(일 보기: 1개월, 월 보기: 1년, 연 보기: 12년).
// 일 보기 본문(요일 헤더 + 날짜 그리드)은 children으로 받는다.
// 모션은 utils/calendarMotion 규칙을 따른다(한 칸 이동: 슬라이드 / 점프·보기 전환: 페이드+확대).
import React, { useEffect, useRef, useState } from "react";
import { Box, ButtonBase, Chip, IconButton, Typography } from "@mui/material";
import { ArrowDropDown, ArrowDropUp, ChevronLeft, ChevronRight } from "@mui/icons-material";
import dayjs from "dayjs";
import { useDarkMode } from "../App";
import { motionForDistance, motionSx, nextMotion } from "../utils/calendarMotion";
import useHorizontalSwipe from "../hooks/useHorizontalSwipe";

const YEAR_PAGE = 12;

// { year, month } → 비교용 단일 정수
const toYm = (y, m) => y * 12 + m;

export default function MonthCalendarNav({
  year,
  month,
  onChange,          // (year, month, { jump }) — jump: 화살표/스와이프가 아닌 점프(월·연 선택, 오늘)
  minYm,             // { year, month } — 월 보기에서 이보다 이전 달은 흐리게(선택 불가)
  maxYm,             // { year, month } — 이보다 이후 달은 흐리게
  allowYearView = false,
  children,
}) {
  const { darkMode } = useDarkMode();
  const today = dayjs();
  const [view, setView] = useState("day");
  const [pickerYear, setPickerYear] = useState(year);
  const [yearPageStart, setYearPageStart] = useState(year - (year % YEAR_PAGE));
  const [motion, setMotion] = useState(null);
  const motionTimer = useRef(null);

  // 바깥에서 달이 바뀌면(알림 진입 등) 일 보기로 복귀
  useEffect(() => {
    setView("day");
  }, [year, month]);

  useEffect(() => () => clearTimeout(motionTimer.current), []);

  const playMotion = (type, vibrate = false) => {
    if (!type) return;
    setMotion(prev => nextMotion(prev, type));
    // 진동 피드백 (모바일) — 한 칸 이동에만
    if (vibrate && navigator.vibrate) {
      navigator.vibrate(30);
    }
    clearTimeout(motionTimer.current);
    motionTimer.current = setTimeout(() => setMotion(null), 300);
  };

  const isOutOfRange = (y, m) =>
    (minYm && toYm(y, m) < toYm(minYm.year, minYm.month)) ||
    (maxYm && toYm(y, m) > toYm(maxYm.year, maxYm.month));

  const isYearOutOfRange = (y) =>
    (minYm && y < minYm.year) || (maxYm && y > maxYm.year);

  // 점프: 바로 옆 달이면 슬라이드, 그보다 멀면 페이드+확대.
  // 같은 달을 고르면 이동 없이 보기만 일 보기로 돌아온다(보기 전환 모션).
  const jumpTo = (y, m) => {
    const diff = toYm(y, m) - toYm(year, month);
    const fromPicker = view !== "day";
    setView("day");
    if (diff === 0) {
      if (fromPicker) playMotion("zoom");
      return;
    }
    playMotion(motionForDistance(diff));
    onChange(y, m, { jump: true });
  };

  // step: -1(이전) / +1(다음)
  const move = (step) => {
    playMotion(motionForDistance(step), true);
    if (view === "day") {
      const t = toYm(year, month) + step;
      onChange(Math.floor(t / 12), t % 12, { jump: false });
    } else if (view === "month") {
      setPickerYear(pickerYear + step);
    } else {
      setYearPageStart(yearPageStart + step * YEAR_PAGE);
    }
  };

  const handleTitleClick = () => {
    playMotion("zoom");
    if (view === "day") {
      setPickerYear(year);
      setView("month");
    } else if (view === "month" && allowYearView) {
      setYearPageStart(pickerYear - (pickerYear % YEAR_PAGE));
      setView("year");
    } else {
      setView("day");
    }
  };

  const swipeHandlers = useHorizontalSwipe(move);

  const isViewingToday = view === "day" && year === today.year() && month === today.month();

  const title =
    view === "day"
      ? `${year}년 ${String(month + 1).padStart(2, "0")}월`
      : view === "month"
        ? `${pickerYear}년`
        : `${yearPageStart} – ${yearPageStart + YEAR_PAGE - 1}`;

  const selectedBg = darkMode ? "#1565c0" : "#1976d2";

  // 월/연 선택 칸 — 선택: 파란 채움, 오늘이 속한 칸: 주황 테두리(일 보기 "오늘" 표기와 동일)
  const pickerCell = ({ key, label, selected, isCurrent, disabled, onClick }) => (
    <ButtonBase
      key={key}
      disabled={disabled}
      onClick={onClick}
      sx={{
        height: 52,
        borderRadius: 2,
        fontSize: "0.95rem",
        fontWeight: selected || isCurrent ? "bold" : "normal",
        bgcolor: selected
          ? selectedBg
          : (darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.035)"),
        color: selected ? "#fff" : "inherit",
        border: isCurrent && !selected ? "1.5px solid #ff9800" : "1.5px solid transparent",
        opacity: disabled ? 0.35 : 1,
        transition: "background-color 0.2s",
        "&:hover": selected ? {} : { backgroundColor: darkMode ? "rgba(144, 202, 249, 0.15)" : "#e3f2fd" },
      }}
    >
      {label}
    </ButtonBase>
  );

  return (
    <Box
      {...swipeHandlers}
      sx={motionSx(motion, "& .calendar-content")}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <IconButton onClick={() => move(-1)} aria-label="이전">
          <ChevronLeft />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
          <ButtonBase
            onClick={handleTitleClick}
            aria-label={view === "day" ? "월 선택" : view === "month" && allowYearView ? "연도 선택" : "달력으로 돌아가기"}
            sx={{ borderRadius: 2, pl: 1, pr: 0.25, py: 0.25, "&:hover": { bgcolor: "action.hover" } }}
          >
            <Typography variant="h6" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
              {title}
            </Typography>
            {view === "day" ? <ArrowDropDown /> : <ArrowDropUp />}
          </ButtonBase>
          {!isViewingToday && (
            <Chip
              label="오늘"
              size="small"
              variant="outlined"
              onClick={() => jumpTo(today.year(), today.month())}
              sx={{ height: 24, fontSize: "0.75rem" }}
            />
          )}
        </Box>
        <IconButton onClick={() => move(1)} aria-label="다음">
          <ChevronRight />
        </IconButton>
      </Box>

      <Box className="calendar-content">
        {view === "day" && children}

        {view === "month" && (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5, py: 1 }}>
            {Array.from({ length: 12 }, (_, m) =>
              pickerCell({
                key: m,
                label: `${m + 1}월`,
                selected: pickerYear === year && m === month,
                isCurrent: pickerYear === today.year() && m === today.month(),
                disabled: isOutOfRange(pickerYear, m),
                onClick: () => jumpTo(pickerYear, m),
              })
            )}
          </Box>
        )}

        {view === "year" && (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5, py: 1 }}>
            {Array.from({ length: YEAR_PAGE }, (_, i) => {
              const y = yearPageStart + i;
              return pickerCell({
                key: y,
                label: `${y}`,
                selected: y === pickerYear,
                isCurrent: y === today.year(),
                disabled: isYearOutOfRange(y),
                onClick: () => {
                  playMotion("zoom");
                  setPickerYear(y);
                  setView("month");
                },
              });
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}
